import { forwardRef, useLayoutEffect, useRef, useState } from "react";

import {
  Box,
  BoxProps,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Row,
  Text,
  useDisclosure,
  TextWithTooltip,
} from "@hightouch/htui";
import { ChartTooltipWrapper } from "src/components/charts/chart-tooltip";

const LEGEND_ITEM_MIN_WIDTH = 120;
// 16px gap + 120px item width
const EFFECTIVE_ITEM_MIN_WIDTH = 136;

export type LegendProps = {
  series: {
    key: string;
    legendName: string;
    color: string;
    legendOrder?: number;
  }[];
  onHover: (key: string) => void;
  onClick?: (key: string) => void;
};

export const Legend = ({
  series: rawSeries,
  onHover,
  onClick,
}: LegendProps) => {
  const series = rawSeries.some((s) => s.legendOrder != null)
    ? rawSeries.toSorted((a, b) => (a.legendOrder ?? 0) - (b.legendOrder ?? 0))
    : rawSeries;
  const ref = useRef<HTMLDivElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [visibleChildCount, setVisibleChildCount] = useState(0);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useLayoutEffect(() => {
    const width = ref.current?.offsetWidth ?? 0;
    setVisibleChildCount(Math.floor(width / EFFECTIVE_ITEM_MIN_WIDTH));
  }, [ref.current?.offsetWidth]);

  const visibleChildren = series.slice(0, visibleChildCount);
  const hiddenChildren = series.slice(visibleChildCount);

  return (
    <Row
      gap={2}
      align="start"
      justify="space-between"
      pr={12} // Right padding keeps legend contained within chart bounds, and not beyond the axes
      width="100%"
      textStyle="mono"
      onMouseLeave={onClose}
    >
      <Row
        ref={ref}
        gap={2}
        justifyItems="start"
        width="100%"
        pb={6} // Bottom spacing needs to remain on these items so the shadow is visible
        overflow="hidden"
      >
        {visibleChildren.map(({ key, legendName, color }, index) => (
          <LegendItemWrapper
            key={key}
            onMouseEnter={() => onHover(key)}
            onClick={onClick ? () => onClick(key) : undefined}
            cursor={onClick ? "pointer" : undefined}
          >
            <ColorIcon color={color} />
            <TextWithTooltip
              size="sm"
              variant="monospace"
              color="text.secondary"
              message={legendName}
              placement={
                index === visibleChildren.length - 1 ? "top-start" : "top"
              }
            >
              {legendName}
            </TextWithTooltip>
          </LegendItemWrapper>
        ))}
      </Row>

      {hiddenChildren.length > 0 && (
        <Popover isLazy isOpen={isOpen} onClose={onClose}>
          <PopoverTrigger>
            <Row role="tooltip" mb={6} onMouseEnter={onOpen}>
              <LegendItemWrapper minW={undefined}>
                <Text size="sm" color="text.secondary" whiteSpace="nowrap">
                  {hiddenChildren.length} more
                </Text>
              </LegendItemWrapper>
            </Row>
          </PopoverTrigger>

          <PopoverContent borderRadius="lg" border="none" maxHeight="300px">
            <ChartTooltipWrapper onMouseLeave={onClose} overflowY="auto">
              {hiddenChildren.map(({ key, legendName, color }) => (
                <Row key={key} align="start" gap={2}>
                  {/* Additional wrapper to ensure height lines up with text */}
                  <Row align="center" justify="center" height={4}>
                    <ColorIcon color={color} />
                  </Row>
                  <Text color="white" size="sm">
                    {legendName}
                  </Text>
                </Row>
              ))}
            </ChartTooltipWrapper>
          </PopoverContent>
        </Popover>
      )}
    </Row>
  );
};

const ColorIcon = ({ color }: { color: string }) => (
  <Box bg={color} borderRadius="3px" boxSize={2} flexShrink={0} />
);

export const LegendItemWrapper = forwardRef<HTMLDivElement, BoxProps>(
  ({ children, ...props }, ref) => {
    return (
      <Row
        ref={ref}
        alignItems="center"
        borderRadius="full"
        borderWidth="hairline"
        borderStyle="solid"
        borderColor="base.divider"
        boxShadow="buttonShadowLight"
        gap={2}
        justify="start"
        py={1}
        px={2}
        minW={LEGEND_ITEM_MIN_WIDTH}
        maxW="100%"
        overflow="hidden"
        {...props}
      >
        {children}
      </Row>
    );
  },
);

LegendItemWrapper.displayName = "LegendItemWrapper";
